import { SourceChoiceEnum } from 'api';
import { PriceAndCurrencyField, SelectField, type SelectOption, TextField, useFormContext } from 'form';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { Grid, Typography } from 'ui';

import { useTransitTimeEstimator, useTransportEstimator } from '../../hooks';

const getTransportCostSourceOptions = (t: I18nTranslateFunction<string>): SelectOption[] => [
  {
    label: t('profitabilityCalculator.costs.options.transportCostSource.estimate'),
    value: SourceChoiceEnum.ESTIMATE,
  },
  {
    label: t('profitabilityCalculator.costs.options.transportCostSource.logisticsQuote'),
    value: SourceChoiceEnum.LOGISTICS_QUOTE,
  },
  {
    label: t('profitabilityCalculator.costs.options.transportCostSource.other'),
    value: SourceChoiceEnum.OTHER,
  },
];

export const CostsSection = () => {
  const { t } = useTranslation('orders');
  const { watch } = useFormContext();
  const { estimateTransportPrice } = useTransportEstimator();
  const { estimateTransitTime } = useTransitTimeEstimator();
  const watchTransportCostSource = watch('transport_cost_source');

  const estimateTransportAndTransitTime = () => {
    estimateTransportPrice();
    estimateTransitTime();
  };
  // disabling transport cost input when ESTIMATE source is selected - it
  // will be pulled automatically in this case
  const shouldDisableTransportCostInput = watchTransportCostSource === SourceChoiceEnum.ESTIMATE;
  const transportCostSourceOptions = getTransportCostSourceOptions(t);

  return (
    <>
      <Typography variant="h5">{t('profitabilityCalculator.costs.title')}</Typography>
      <Grid container spacing={4}>
        <Grid
          size={{
            md: 3,
            xs: 12,
          }}
        >
          <PriceAndCurrencyField
            slotProps={{
              currency: {
                name: 'seller_currency',
              },
              price: {
                label: t('profitabilityCalculator.costs.labels.sellerPricePerTonne'),
                name: 'seller_price_per_tonne',
                required: true,
              },
            }}
          />
        </Grid>
        <Grid
          size={{
            md: 3,
            xs: 12,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <PriceAndCurrencyField
                disabled={shouldDisableTransportCostInput}
                slotProps={{
                  currency: {
                    name: 'shipping_cost_per_load_currency',
                    onChange: estimateTransportAndTransitTime,
                  },
                  price: {
                    label: t('profitabilityCalculator.costs.labels.shippingAndCustomsCostPerLoad'),
                    name: 'shipping_cost_per_load',
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label={t('profitabilityCalculator.costs.labels.estimatedTransitTimeDays')}
                type="number"
                name="estimated_transit_time_days"
              />
            </Grid>
            <Grid size={12}>
              <SelectField
                name="transport_cost_source"
                label={t('profitabilityCalculator.costs.labels.shippingCostSource')}
                options={transportCostSourceOptions}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          size={{
            md: 3,
            xs: 12,
          }}
        >
          <PriceAndCurrencyField
            slotProps={{
              currency: {
                name: 'buyer_agent_commission_currency',
              },
              price: {
                label: t('profitabilityCalculator.costs.labels.buyerAgentCommissionPerTonne'),
                name: 'buyer_agent_commission',
              },
            }}
          />
        </Grid>
        <Grid
          size={{
            md: 3,
            xs: 12,
          }}
        >
          <PriceAndCurrencyField
            slotProps={{
              currency: {
                name: 'seller_agent_commission_currency',
              },
              price: {
                label: t('profitabilityCalculator.costs.labels.sellerAgentCommissionPerTonne'),
                name: 'seller_agent_commission',
              },
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
